import React, { useState, useEffect, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { Grid, Button, FormGroup } from '@mui/material'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import InputField from 'components/form/InputField'
import RadioButtonGroup from 'components/form/RadioButtonGroup'
import CountrySelect from 'components/form/CountrySelect'
import CountryStateSelect from 'components/form/CountryStateSelect'
import SubmitButton from 'components/form/SubmitButton'
import ProviderSelect from 'components/form/ProviderSelect'
import { selectStates, clearStates, clearCountryStates } from 'store/countryStates/slice'
import { getStates } from 'store/countryStates/thunks'
import { saveSearchFilters as clearClubFilters } from 'store/clubs/slice'
import { saveSearchFilters as clearCourseFilters } from 'store/courses/slice'
import { UseFormReturn } from 'react-hook-form'
import { getConfiguration } from 'store/authentication/thunks'
import { selectAuth } from 'store/authentication/slice'

export enum SEARCH_TYPE {
  Club = 'club',
  Course = 'course',
}

interface Props {
  form: UseFormReturn<CourseClubSearchForm>
  type: SEARCH_TYPE.Club | SEARCH_TYPE.Course
  searchStatus: RequestStatus
  handleSearch: (data: any) => void
  toggleModal?: () => void
}

export default function ClubCourseSearchForm({
  form,
  type,
  handleSearch,
  toggleModal,
  searchStatus,
}: Props): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { handleSubmit, register, control, watch, setValue, reset } = form
  const [countriesWithStates, setCountriesWithStates] = useState<Country[]>([])
  const [courseChecksAdded, setCourseChecksAdded] = useState<boolean>(false)
  const countryStates = useSelector(selectStates)
  const courseFieldsDirty = useCallback(() => {
    return watch('courseName') || watch('courseId')
  }, [watch])
  const { siteConfig } = useSelector(selectAuth)

  const initialFilters: CourseClubSearchForm = useMemo(() => {
    return {
      countries: [],
      states: [],
      city: '',
      clubId: '',
      clubName: '',
      courseId: '',
      courseName: '',
      activeClubs: 'true',
      activeCourses: type === SEARCH_TYPE.Course ? 'true' : '',
      officialCourses: type === SEARCH_TYPE.Course ? 'true' : '',
      providerId: null,
    }
  }, [type])

  useEffect(() => {
    if (!siteConfig) {
      dispatch(getConfiguration())
    }
  }, [])

  useEffect(() => {
    setValue('activeClubs', 'true')
    if (type === SEARCH_TYPE.Course) {
      setValue('officialCourses', 'true')
      setValue('activeCourses', 'true')
      setCourseChecksAdded(true)
    }
  }, [type, setValue])

  useEffect(() => {
    if (courseFieldsDirty() && !courseChecksAdded) {
      setValue('activeCourses', 'true')
      setValue('officialCourses', 'true')
      setCourseChecksAdded(true)
    }
  }, [courseFieldsDirty, setValue, courseChecksAdded])

  const handleCountryChange = (countries: Country[]) => {
    if (countries.length === 0) {
      setValue('states', [])
      setCountriesWithStates([])
      dispatch(clearStates())
    }

    const removedCountries = countriesWithStates.filter((country: Country) => !countries.includes(country))
    removedCountries.map((country: Country) => {
      setCountriesWithStates(countriesWithStates.filter((c: Country) => c !== country))
      dispatch(clearCountryStates(country))
      return country
    })

    countries.map((country: Country) => {
      if (country.hasStates && !countriesWithStates.includes(country)) {
        setCountriesWithStates([...countriesWithStates, country])
        dispatch(getStates(country.id))
      }
      return country
    })
  }

  const getAddNewButtonText = () => {
    const base = intl.formatMessage({ id: 'strings.addNew' })
    if (type === SEARCH_TYPE.Course) {
      return `${base} ${intl.formatMessage({ id: 'strings.course' }).toLowerCase()}`
    }
    return `${base} ${intl.formatMessage({ id: 'strings.club' }).toLowerCase()}`
  }

  const clearFilters = () => {
    if (type === SEARCH_TYPE.Club) {
      clearClubsData()
      return
    }
    clearCoursesData()
  }

  const resetForm = () => {
    reset(initialFilters)
    setCountriesWithStates([])
    dispatch(clearStates())
  }

  const clearClubsData = () => {
    dispatch(clearClubFilters({}))
    resetForm()
  }

  const clearCoursesData = () => {
    dispatch(clearCourseFilters({}))
    resetForm()
  }

  return (
    <SearchForm onSubmit={handleSubmit(handleSearch)}>
      <Grid container spacing={5}>
        <FiltersSection item xs={4}>
          <CountrySelect
            name="countries"
            control={control}
            initialValue={watch('countries') || []}
            onlyCountriesWithClubs
            label={intl.formatMessage({ id: 'strings.country' })}
            handleCountryChange={handleCountryChange}
            multiple
          />
          <Spacer />
          <CountryStateSelect
            name="states"
            control={control}
            countryStates={countryStates}
            disabled={countriesWithStates.length === 0 && (!watch('states') || watch('states').length === 0)}
            value={watch('states')}
            multiple
          />
          <Spacer />
          <CityInput
            name="city"
            type="text"
            register={register}
            label={intl.formatMessage({ id: 'strings.city' })}
            outlined
          />
          <ProviderSelect
            name="providerId"
            control={control}
            value={watch('providerId') ? Number(watch('providerId')) : undefined}
          />
        </FiltersSection>
        <FiltersSection item xs={4}>
          <StyledInputField
            name="clubName"
            type="text"
            register={register}
            label={intl.formatMessage({ id: 'strings.clubsName' })}
            outlined
          />
          <StyledInputField
            name="clubId"
            type="number"
            register={register}
            label={intl.formatMessage({ id: 'strings.clubId' })}
            outlined
          />
          <FormGroup>
            <RadioButtonGroup
              name="activeClubs"
              form={form}
              elements={[
                { label: intl.formatMessage({ id: 'strings.active' }), value: 'true', preSelected: true },
                { label: intl.formatMessage({ id: 'strings.deactivated' }), value: 'false' },
              ]}
            />
          </FormGroup>
        </FiltersSection>
        <FiltersSection item xs={4}>
          <StyledInputField
            name="courseName"
            type="text"
            register={register}
            label={intl.formatMessage({ id: 'strings.coursesName' })}
            outlined
          />
          <StyledInputField
            name="courseId"
            type="number"
            register={register}
            label={intl.formatMessage({ id: 'strings.courseId' })}
            outlined
          />
          <RadioGroupContainer>
            <FormGroup>
              <RadioButtonGroup
                name="officialCourses"
                form={form}
                elements={[
                  {
                    label: intl.formatMessage({ id: 'strings.official' }),
                    value: 'true',
                    preSelected: courseChecksAdded,
                  },
                  { label: intl.formatMessage({ id: 'strings.unofficial' }), value: 'false' },
                ]}
              />
            </FormGroup>
          </RadioGroupContainer>
          <RadioGroupContainer>
            <FormGroup>
              <RadioButtonGroup
                name="activeCourses"
                form={form}
                elements={[
                  {
                    label: intl.formatMessage({ id: 'strings.active' }),
                    value: 'true',
                    preSelected: courseChecksAdded,
                  },
                  { label: intl.formatMessage({ id: 'strings.deactivated' }), value: 'false' },
                ]}
              />
            </FormGroup>
          </RadioGroupContainer>
        </FiltersSection>
        <SearchAndClear item xs={4}>
          <SearchButton status={searchStatus} text={intl.formatMessage({ id: 'strings.search' })} />
          <ClearFilters onClick={clearFilters}>{intl.formatMessage({ id: 'strings.clearFilters' })}</ClearFilters>
        </SearchAndClear>
        <Grid item xs={4}>
          <NewEntityButton onClick={toggleModal}>{getAddNewButtonText()}</NewEntityButton>
        </Grid>
      </Grid>
    </SearchForm>
  )
}

const SearchForm = styled.form`
  padding: 20px 20% 20px 0;

  & .MuiInputLabel-root {
    color: #404040;
    padding: 0;
  }
  & .MuiInputBase-formControl {
    background-color: ${(props) => props.theme.customPalette.lightGray};
    min-height: 40px;
    padding: 5px;
    & .MuiInputBase-input {
      padding: 0 0 0 5px;
    }
  }
`

const SearchButton = styled(SubmitButton)`
  border: 2px solid transparent;
`

const NewEntityButton = styled(Button)`
  width: 50%;
  margin-left: 20px;
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.main};
`

const ClearFilters = styled(Button)`
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.main};
`

const SearchAndClear = styled(Grid)`
  display: flex;
  justify-content: space-between;
`

const FiltersSection = styled(Grid)`
  margin: 20px 0;
`

const StyledInputField = styled(InputField)`
  margin: 5px 0 30px;
`

const CityInput = styled(InputField)`
  margin: 5px 0 20px;
`

const Spacer = styled.div`
  width: 100%;
  height: 20px;
`

const RadioGroupContainer = styled.div`
  display: inline-block;
  margin: 0 20px 20px 0;
`
